import { createContext, useContext, useLayoutEffect, useEffect, type ReactNode } from "react";

import noop from "lodash/noop";

export type ReactSetterArg<T> = T | ((oldArg: T) => T);
export type ReactSetter<T> = (arg: ReactSetterArg<T>) => void;

export const PageContext = createContext<{
    setShowSidebar: ReactSetter<boolean>;
    setPageTitle: ReactSetter<string | undefined>;
    setHeaderActions: ReactSetter<ReactNode>;
}>({ setShowSidebar: noop, setPageTitle: noop, setHeaderActions: noop });

type PageConfigProps = {
    showSidebar?: boolean;
    pageTitle?: string;
};

/**
 * Updates the app layout configuration via PageContext (managed by RVAppLayout).
 *
 * This hook synchronizes app layout state such as sidebar visibility and page title.
 * Note: `pageTitle` controls the app layout title display only, not the browser's document.title.
 * To set the browser title, use React Helmet or similar libraries.
 *
 * @param showSidebar - Whether to show the sidebar (default: true)
 * @param pageTitle - The page title to display in the app layout
 *
 * @example
 * ```tsx
 * usePageConfig({ pageTitle: "Contacts", showSidebar: true });
 * ```
 */
export function usePageConfig({ showSidebar = true, pageTitle }: PageConfigProps = {}): void {
    const { setShowSidebar, setPageTitle } = useContext(PageContext);

    useLayoutEffect(() => {
        setShowSidebar(showSidebar);
    }, [setShowSidebar, showSidebar]);

    useEffect(() => setPageTitle(pageTitle), [pageTitle, setPageTitle]);
}

/**
 * Injects content into the app header's actions slot (managed by RVAppLayout).
 *
 * This lets a page register its own header controls without the layout needing to know
 * about the page. The slot is cleared automatically when the calling component unmounts.
 *
 * @param headerActions - The content to render in the header actions slot (or `null`/`undefined` for none)
 *
 * @example
 * ```tsx
 * useHeaderActions(<AgentNavBarButtons />);
 * ```
 */
export function useHeaderActions(headerActions: ReactNode): void {
    const { setHeaderActions } = useContext(PageContext);

    useEffect(() => {
        setHeaderActions(headerActions);
        return () => setHeaderActions(null);
    }, [headerActions, setHeaderActions]);
}
